import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import layoutBgImg from "../../Images/layout/others.jpg";
import Layout from "../../Component/layout/main-layout";
import { useTranslation } from "react-i18next";
import GlobalLayout from "../../Component/layout/global-layout";
import { RegisterUser } from "../../api/Queries/post";
import { Context } from "../../Store/context";

const Registration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useContext(Context);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    repeat: "",
  });

  const [errors, setErrors] = useState({});

  const change = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.first_name.trim()) {
      nextErrors.first_name = t("required");
    }
    if (!form.last_name.trim()) {
      nextErrors.last_name = t("required");
    }
    if (!form.username.trim()) {
      nextErrors.username = t("required");
    }
    if (!form.email.trim()) {
      nextErrors.email = t("required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = t("invalid_email");
    }
    if (!form.password) {
      nextErrors.password = t("required");
    } else if (form.password.length < 6) {
      nextErrors.password = t("password_min_length");
    }
    if (!form.repeat) {
      nextErrors.repeat = t("required");
    } else if (form.password !== form.repeat) {
      nextErrors.repeat = t("passwords_not_match");
    }
    return nextErrors;
  };

  const getErrorMessage = (error) => {
    const data = error?.data || error?.response?.data;
    if (typeof data === "string") return data;
    if (data?.detail) return data.detail;
    if (data?.message) return data.message;
    const firstKey =
      data && typeof data === "object" ? Object.keys(data)[0] : null;
    if (firstKey) {
      const value = data[firstKey];
      if (Array.isArray(value)) return String(value[0]);
      if (typeof value === "string") return value;
    }
    return error?.message || t("not_success");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      username: form.username.trim(),
      email: form.email.trim(),
      password: form.password,
    };
    setIsLoading(true);
    try {
      await RegisterUser(payload);
      showSuccess(t("log_in_system"));
      navigate("/");
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GlobalLayout
      title={`${t("registration")} - ${t("supreme_court")} ${t("turkmenistan")}`}
      keywords=""
    >
      <div className="mb-20">
        <Layout title={t("registration")} layoutBgImg={layoutBgImg} />

        <div className="flex justify-center w-full">
          <div className="p-4 xl:p-8 mt-8 xl:w-[42%] 
               dark:bg-dark-box w-[90%] rounded bg-white"
          >
            <h1 className="text-center font-bold mb-1 
                text-3xl xl:text-4xl dark:text-white"
            >
              {t("registration")}
            </h1>

            <p className="text-center text-text text-sm dark:text-dark-text mb-6">
              {t("please_registration")}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="xl:flex gap-4">
                <div className="xl:w-1/2">
                  <label className="text-sm dark:text-dark-text">
                    {t("write_name")}
                  </label>

                  <input
                    type="text"
                    value={form.first_name}
                    onChange={(e) => change("first_name", e.target.value)}
                    placeholder={t("write_name")}
                    className={`w-full px-4 py-2 border bg-secondary mb-1 mt-1 rounded
                                focus:outline-none focus:ring-2 focus:ring-indigo-500
                                dark:bg-dark-box dark:border-dark-border dark:text-dark-text
                                ${
                                  errors.first_name
                                    ? "border-red"
                                    : "border-border"
                                }`}
                  />

                  {errors.first_name && (
                    <p className="text-red text-xs mb-2">
                      {errors.first_name}
                    </p>
                  )}
                </div>

                <div className="xl:w-1/2">
                  <label className="text-sm dark:text-dark-text">
                    {t("write_surname")}
                  </label>

                  <input
                    type="text"
                    value={form.last_name}
                    onChange={(e) => change("last_name", e.target.value)}
                    placeholder={t("write_surname")}
                    className={`w-full px-4 py-2 border bg-secondary mb-1 mt-1 rounded
                                focus:outline-none focus:ring-2 focus:ring-indigo-500
                                dark:bg-dark-box dark:border-dark-border dark:text-dark-text
                                ${
                                  errors.last_name
                                    ? "border-red"
                                    : "border-border"
                                }`}
                  />

                  {errors.last_name && (
                    <p className="text-red text-xs mb-2">
                      {errors.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-2">
                <label className="text-sm dark:text-dark-text">
                  {t("username") || "Username"}
                </label>

                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => change("username", e.target.value)}
                  placeholder={t("username") || "Username"}
                  autoComplete="username"
                  className={`w-full px-4 py-2 border bg-secondary mb-1 mt-1 rounded
                              focus:outline-none focus:ring-2 focus:ring-indigo-500
                              dark:bg-dark-box dark:border-dark-border dark:text-dark-text
                              ${
                                errors.username
                                  ? "border-red"
                                  : "border-border"
                              }`}
                />
                {errors.username && (
                  <p className="text-red text-xs mb-2">
                    {errors.username}
                  </p>
                )}
              </div>

              <div className="mt-2">
                <label className="text-sm dark:text-dark-text">
                  {t("email")}
                </label>

                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => change("email", e.target.value)}
                  placeholder="example@gmail.com"
                  autoComplete="email"
                  className={`w-full px-4 py-2 border bg-secondary mb-1 mt-1 rounded
                              focus:outline-none focus:ring-2 focus:ring-indigo-500
                              dark:bg-dark-box dark:border-dark-border dark:text-dark-text
                              ${
                                errors.email
                                  ? "border-red"
                                  : "border-border"
                              }`}
                />

                {errors.email && (
                  <p className="text-red text-xs mb-2">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="mt-2">
                <label className="text-sm dark:text-dark-text">
                  {t("password")}
                </label>

                <div
                  className={`flex items-center border bg-secondary mb-1 mt-1 rounded
                              focus-within:ring-2 focus-within:ring-indigo-500
                              dark:bg-dark-box dark:border-dark-border
                              ${
                                errors.password
                                  ? "border-red"
                                  : "border-border"
                              }`}
                >
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => change("password", e.target.value)}
                    placeholder="******"
                    autoComplete="new-password"
                    className="w-full px-4 py-2 bg-transparent focus:outline-none dark:text-dark-text"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="px-3 text-sm text-primary dark:text-white"
                  >
                    {showPassword ? t("hide") : t("show")}
                  </button>
                </div>

                {errors.password && (
                  <p className="text-red text-xs mb-2">
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="mt-2">
                <label className="text-sm dark:text-dark-text">
                  {t("repeat_password") || "Paroly gaýtala"}
                </label>

                <div
                  className={`flex items-center border bg-secondary mb-1 mt-1 rounded
                              focus-within:ring-2 focus-within:ring-indigo-500
                              dark:bg-dark-box dark:border-dark-border
                              ${
                                errors.repeat
                                  ? "border-red"
                                  : "border-border"
                              }`}
                >
                  <input
                    type={showRepeatPassword ? "text" : "password"}
                    value={form.repeat}
                    onChange={(e) => change("repeat", e.target.value)}
                    placeholder="******"
                    autoComplete="new-password"
                    className="w-full px-4 py-2 bg-transparent focus:outline-none dark:text-dark-text"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowRepeatPassword((prev) => !prev)
                    }
                    className="px-3 text-sm text-primary dark:text-white"
                  >
                    {showRepeatPassword ? t("hide") : t("show")}
                  </button>
                </div>

                {errors.repeat && (
                  <p className="text-red text-xs mb-2">
                    {errors.repeat}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`relative text-white bg-primary px-6 
                            w-full mt-6 py-2 rounded min-h-[40px]
                            flex justify-center items-center
                            ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <span className={isLoading ? "invisible" : "visible"}>
                  {t("create_account") || t("registration")}
                </span>

                {isLoading && (
                  <span
                    className="absolute w-4 h-4 border-2 border-white 
                               border-t-transparent rounded-full animate-spin"
                  />
                )}
              </button>

              <div className="mt-4 text-sm dark:text-dark-text">
                <span>{t("have_account") || "Hasabyňyz barmy?"}</span>

                <Link
                  to="/auth/login"
                  className="text-primary dark:text-white underline ml-1"
                >
                  {t("log_in")}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </GlobalLayout>
  );
};

export default Registration;